// src/components/AddMedicationModal.jsx
// Modal for adding medications with RxNorm autocomplete and dosage options

import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Pill, Plus, Loader2, Check } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function AddMedicationModal({ isOpen, onClose, clientId, onSave }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Dosage options from RxNorm
  const [dosageOptions, setDosageOptions] = useState([]);
  const [loadingDosages, setLoadingDosages] = useState(false);

  // Form fields
  const [dosage, setDosage] = useState('');
  const [quantity, setQuantity] = useState('');
  const [frequency, setFrequency] = useState('');
  const [refillFrequency, setRefillFrequency] = useState('');
  const [notes, setNotes] = useState('');
  const [pharmacyType, setPharmacyType] = useState('retail'); // retail, mail_order, preferred

  const searchInputRef = useRef(null);
  const searchTimeout = useRef(null);

  // Focus search input on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Debounced drug search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `${API_BASE}/medications/search?q=${encodeURIComponent(searchQuery)}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.ok) {
          setSearchResults(data.drugs || []);
        }
      } catch (err) {
        console.error('Drug search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  // Fetch dosage options when drug is selected
  const fetchDosageOptions = async (rxcui) => {
    if (!rxcui) return;

    setLoadingDosages(true);
    try {
      const res = await fetch(
        `${API_BASE}/medications/dosages/${rxcui}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (data.ok && data.dosages) {
        setDosageOptions(data.dosages);
      }
    } catch (err) {
      console.error('Fetch dosages error:', err);
    } finally {
      setLoadingDosages(false);
    }
  };

  const handleSelectDrug = (drug) => {
    setSelectedDrug(drug);
    setSearchQuery(drug.name);
    setSearchResults([]);
    setShowForm(true);
    setDosageOptions([]);
    setDosage('');

    // Fetch dosage options for this drug
    if (drug.rxcui) {
      fetchDosageOptions(drug.rxcui);
    }
  };

  const handleSave = async () => {
    if (!selectedDrug?.name && !searchQuery) {
      setError('Please select or enter a medication name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/medications/client/${clientId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          medication_name: selectedDrug?.name || searchQuery,
          rxnorm_code: selectedDrug?.rxcui || null,
          dosage,
          quantity,
          frequency,
          refill_frequency: refillFrequency,
          notes,
          pharmacy_type: pharmacyType
        })
      });

      const data = await res.json();

      if (data.ok) {
        setSuccess(true);
        setTimeout(() => {
          // Include selected pharmacy type for copay display
          const formularyWithPharmacy = {
            ...data.formularyData,
            selectedPharmacyType: pharmacyType
          };
          onSave?.(data.medication, formularyWithPharmacy);
          handleClose();
        }, 1000);
      } else {
        setError(data.error || 'Failed to save medication');
      }
    } catch (err) {
      console.error('Save medication error:', err);
      setError('Failed to save medication');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSearchQuery('');
    setSearchResults([]);
    setSelectedDrug(null);
    setShowForm(false);
    setDosage('');
    setQuantity('');
    setFrequency('');
    setRefillFrequency('');
    setNotes('');
    setPharmacyType('retail');
    setError(null);
    setSuccess(false);
    setDosageOptions([]);
    onClose();
  };

  // Frequency presets (how often to take)
  const frequencyOptions = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'Every 12 hours',
    'Weekly',
    'As needed (PRN)'
  ];

  // Refill frequency presets
  const refillFrequencyOptions = [
    '30 days',
    '60 days',
    '90 days',
    'Monthly',
    'Quarterly',
    'As needed'
  ];

  // Pharmacy type options for copay calculation
  const pharmacyTypeOptions = [
    { value: 'retail', label: 'Retail Pharmacy (30-day)' },
    { value: 'preferred', label: 'Preferred Retail Pharmacy' },
    { value: 'mail_order', label: 'Mail Order (90-day)' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Pill className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Add Medication</h2>
              <p className="text-blue-100 text-sm">Search and add to client profile</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Medication Added!</h3>
              <p className="text-gray-500 mt-2">
                {selectedDrug?.name || searchQuery} has been added to the client's profile.
              </p>
            </div>
          ) : (
            <>
              {/* Drug Search */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Medication
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedDrug(null);
                      setDosageOptions([]);
                      if (e.target.value.length < 2) {
                        setShowForm(false);
                      }
                    }}
                    placeholder="Type medication name (e.g., Lisinopril)..."
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />
                  )}
                </div>

                {/* Search Results Dropdown */}
                {searchResults.length > 0 && !selectedDrug && (
                  <div className="absolute z-10 mt-1 w-[calc(100%-3rem)] bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((drug) => (
                      <button
                        key={drug.rxcui}
                        onClick={() => handleSelectDrug(drug)}
                        className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{drug.name}</div>
                        {drug.tty && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {drug.tty === 'IN' && 'Ingredient'}
                            {drug.tty === 'BN' && 'Brand Name'}
                            {drug.tty === 'SCD' && 'Clinical Drug'}
                            {drug.tty === 'SBD' && 'Branded Drug'}
                            {!['IN', 'BN', 'SCD', 'SBD'].includes(drug.tty) && drug.tty}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* No results hint */}
                {searchQuery.length >= 2 && !searching && searchResults.length === 0 && !selectedDrug && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">
                      No matches found. You can still add "{searchQuery}" manually.
                    </p>
                    <button
                      onClick={() => {
                        setSelectedDrug({ name: searchQuery, rxcui: null });
                        setShowForm(true);
                      }}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      + Add as custom medication
                    </button>
                  </div>
                )}
              </div>

              {/* Medication Details Form */}
              {showForm && selectedDrug && (
                <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
                  <div className="p-3 bg-blue-50 rounded-lg mb-4">
                    <div className="flex items-center gap-2">
                      <Pill className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-900">{selectedDrug.name}</span>
                    </div>
                    {selectedDrug.rxcui && (
                      <p className="text-xs text-blue-600 mt-1">RxNorm: {selectedDrug.rxcui}</p>
                    )}
                  </div>

                  {/* Dosage - Dropdown with RxNorm options or manual entry */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dosage/Strength
                    </label>
                    {loadingDosages ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading dosage options...
                      </div>
                    ) : dosageOptions.length > 0 ? (
                      <select
                        value={dosage}
                        onChange={(e) => setDosage(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select dosage...</option>
                        {dosageOptions.map((opt) => (
                          <option key={opt.rxcui || opt.strength} value={opt.strength}>
                            {opt.strength}
                          </option>
                        ))}
                        <option value="other">Other (enter manually)</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={dosage}
                        onChange={(e) => setDosage(e.target.value)}
                        placeholder="e.g., 10 MG, 20 MG tablet"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                    {dosage === 'other' && (
                      <input
                        type="text"
                        value=""
                        onChange={(e) => setDosage(e.target.value)}
                        placeholder="Enter dosage manually..."
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="text"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      placeholder="e.g., 30, 60, 90"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Frequency (how often to take) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      How Often (Directions)
                    </label>
                    <select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select frequency...</option>
                      {frequencyOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Refill Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refill Frequency
                    </label>
                    <select
                      value={refillFrequency}
                      onChange={(e) => setRefillFrequency(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select refill frequency...</option>
                      {refillFrequencyOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Pharmacy Type (for copay calculation) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pharmacy Type
                    </label>
                    <select
                      value={pharmacyType}
                      onChange={(e) => setPharmacyType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {pharmacyTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Used to determine copay amount</p>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes <span className="text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any additional notes..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={(!selectedDrug && !searchQuery) || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                (selectedDrug || searchQuery) && !saving
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Medication
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
